<template>
  <transition name="fade">
    <div
      v-if="show"
      ref="inboxRef"
      :class="[
        'notification-inbox',
        { 'notification-inbox--from-alert': fromAlert },
      ]"
      :style="inlineStyle"
    >
      <div class="inbox-header">
        <h3>Notifications</h3>
      </div>
      <div class="inbox-content">
        <div
          v-if="notificationStore.notifications.length === 0"
          class="empty-state"
        >
          <p>No notifications yet</p>
        </div>
        <div
          v-for="notification in notificationStore.notifications"
          :key="notification.id"
          :class="['notification-item', { unread: !notification.read }]"
        >
          <div class="notification-content">
            <div class="notification-icon">
              <div class="icon-placeholder">
                <font-awesome-icon :icon="faHandshake" class="handshake-icon" />
              </div>
            </div>
            <div class="notification-body-wrapper">
              <div class="notification-body">
                <div class="notification-main">
                  <span
                    v-if="!notification.read"
                    class="unread-indicator"
                  ></span>
                </div>
                <div class="notification-message">
                  <span
                    v-for="(line, index) in (
                      notification.content?.detail || ''
                    ).split('\n')"
                    :key="index"
                    :class="
                      index === 0
                        ? 'notification-message-main'
                        : 'notification-message-sub'
                    "
                  >
                    {{ line }}
                  </span>
                </div>
              </div>
              <div class="notification-meta">
                <span class="notification-time">{{
                  formatTime(notification.createdAt)
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useNotificationStore } from '@/stores/notification';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faHandshake } from '@fortawesome/free-solid-svg-icons/faHandshake';
import { PostHogUtils } from '@/core/PostHogUtils';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  fromAlert: {
    type: Boolean,
    default: false,
  },
  triggerSelectors: {
    type: Array,
    default: () => ['.notification-bell'],
  },
  anchorRect: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const notificationStore = useNotificationStore();
const inboxRef = ref(null);
const inlineStyle = ref({});

function formatTime(timestamp) {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}m ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  } else {
    return date.toLocaleDateString();
  }
}

// Click outside handler
function handleClickOutside(event) {
  if (inboxRef.value && !inboxRef.value.contains(event.target)) {
    // Check if the click was on any configured trigger element (e.g., bell icons) to avoid immediate close
    const isWithinTrigger = props.triggerSelectors.some((selector) => {
      const triggerElements = document.querySelectorAll(selector);
      return Array.from(triggerElements).some((element) =>
        element.contains(event.target),
      );
    });

    if (!isWithinTrigger) emit('close');
  }
}

// Escape key handler
function handleEscapeKey(event) {
  if (event.key === 'Escape' && props.show) {
    emit('close');
  }
}

async function updatePosition() {
  if (!props.show || props.fromAlert) {
    inlineStyle.value = {};
    return;
  }

  const rect = props.anchorRect;
  if (!rect) {
    inlineStyle.value = {};
    return;
  }

  await nextTick();
  const inboxElement = inboxRef.value;
  if (!inboxElement) {
    inlineStyle.value = {};
    return;
  }

  const margin = 12;
  const width = inboxElement.offsetWidth || 0;
  const height = inboxElement.offsetHeight || 0;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top = rect.bottom + margin;
  if (top + height + margin > viewportHeight) {
    top = Math.max(margin, rect.top - height - margin);
  }

  let left = rect.left + rect.width / 2 - width / 2;
  left = Math.max(margin, Math.min(left, viewportWidth - width - margin));

  inlineStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    right: 'auto',
  };
}

watch(
  () => [props.show, props.fromAlert, props.anchorRect],
  () => {
    updatePosition();
  },
  { deep: true },
);

// One event per open, not per render: this component stays mounted for the
// life of the header and only toggles `show`, so watching the closed -> open
// edge is the dedupe — repositioning, re-renders and store refreshes can't
// re-fire it. Every entry point (header bell, home bell, alert badge) routes
// through this single inbox instance, so they're all covered here.
watch(
  () => props.show,
  (shown, wasShown) => {
    // Opens only. The rows in this list have no click handler - they aren't
    // interactive - so there is no inbox click to record; notification_clicked
    // covers the toast alerts, which are clickable.
    if (shown && !wasShown) PostHogUtils.capture('notification_inbox_opened');
  },
);

onMounted(() => {
  // Add a slight delay to avoid closing immediately when the inbox opens
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside);
  }, 100);
  document.addEventListener('keydown', handleEscapeKey);
  updatePosition();
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleEscapeKey);
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/theme/variables.scss' as theme;

.notification-inbox {
  position: absolute;
  right: 0;
  top: 50px;
  width: 380px;
  max-height: 500px;
  background: theme.$grey-402; // Uses theme variable (light or dark)
  box-shadow: theme.$box-shadow-02;
  border-radius: 8px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  border: theme.$border-02;

  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    right: 0;
    top: 45px;
    width: calc(100vw - 20px);
    max-width: 380px;
  }

  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    width: calc(100vw - 10px);
  }

  // Fixed positioning when opened from alert (e.g., clicking notification badge)
  &--from-alert {
    position: fixed;
    right: 1rem;
    top: 1rem;
    z-index: 10000; // Higher z-index to ensure it appears above everything

    @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
      right: 1rem;
      top: 1rem;
      width: calc(100vw - 2rem);
      max-width: 380px;
    }

    @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
      right: 0.5rem;
      top: 0.5rem;
      width: calc(100vw - 1rem);
    }
  }

  .inbox-header {
    padding: 1rem 1.5rem;
    border-bottom: theme.$border-02;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: theme.$font-weight-semibold;
      color: theme.$black; // Adapts to theme
    }
  }

  .inbox-content {
    overflow-y: auto;
    max-height: 432px;

    .empty-state {
      padding: 3rem 1.5rem;
      text-align: center;
      color: theme.$grey-2400; // Muted text

      p {
        margin: 0;
        font-size: 0.9375rem;
      }
    }

    .notification-item {
      padding: 0;
      border-bottom: theme.$border-02;
      transition: all 0.2s ease;
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.02);
      }

      &.unread {
        background-color: rgba(0, 0, 0, 0.01);

        &:hover {
          background-color: rgba(0, 0, 0, 0.03);
        }
      }

      &:last-child {
        border-bottom: none;
      }

      // Equal padding for all items

      .notification-content {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        padding: 0.75rem 1.25rem;
        transition: all 0.2s ease;
      }

      .notification-icon {
        flex-shrink: 0;
        align-self: center;
        margin-top: 0.25rem;

        .icon-placeholder {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: theme.$color-primary;
          border-radius: 10px;
          transition: transform 0.2s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

          .handshake-icon {
            width: 22px;
            height: 22px;
            color: theme.$white;
            filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
          }
        }
      }

      &:hover .notification-icon .icon-placeholder {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
      }

      .notification-body-wrapper {
        flex: 1;
        min-width: 0;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 1rem;
      }

      .notification-body {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
      }

      .notification-main {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        width: 100%;

        .unread-indicator {
          width: 8px;
          height: 8px;
          background-color: theme.$color-primary;
          border-radius: 50%;
          flex-shrink: 0;
          margin-left: auto;
        }
      }

      .notification-message {
        font-size: 1rem;
        color: theme.$black;
        line-height: 1.6;
        word-wrap: break-word;
        white-space: pre-line;

        .notification-message-main {
          font-weight: theme.$font-weight-bold;
          font-size: 0.875rem;
          color: theme.$black;
          display: block;
          line-height: 1.4;
        }

        .notification-message-sub {
          font-weight: theme.$font-weight-light;
          color: theme.$grey-2500;
          display: block;
          font-size: 0.6875rem; // slightly smaller for secondary line
          opacity: 0.7;
          line-height: 1.4;
        }
      }

      .notification-meta {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        align-self: center;
        margin-top: 0.25rem;
      }

      .notification-time {
        font-size: 0.75rem;
        color: theme.$grey-2501;
        white-space: nowrap;
        font-weight: theme.$font-weight-normal;
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
